/*
 * Copyright (c) 2023 CalAmp Corp. All Rights Reserved
 */
import React, {ReactElement} from 'react';
import isNil from 'lodash.isnil';
import {translate} from '@ion/core/src/locale/locale';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {AugmentedSmarterTrailerSensorData, SensorTypes} from '@ion/core/src/types/entities';
import {getUserUnit} from '@ion/core/src/units/helpers';
import {dateFormatter, timeFormatter} from '@ion/core/src/utils/DateUtil';
import {dlsColor} from '@ion/dls/src/theme/theme';
import WeightWidgetIcon from '../../icons/WeightWidgetIcon';
import {SmarterTrailerWidgetInfo} from '../../types/types';
import {convertWeightSensorData, getRemainingCargoAvailability, getTimeDifference} from './helper';
import WidgetContainer from './WidgetContainer';
import WidgetDescription from './WidgetDescription';

export type WeightWidgetProps = {
  timestamp: string;
  trailerData: AugmentedSmarterTrailerSensorData;
  sensorType?: SensorTypes;
};

/**
 * Defines the Weight Sensor Widget component.
 *
 * ```typescript
 *
 * import {WeightWidget} from '@ion/dls/src/components/smarterTrailer';
 *
 * <WeightWidget trailerData={trailerData} />
 *
 * ```
 */
export const WeightWidget = ({
  timestamp,
  trailerData
}: WeightWidgetProps) => {
  if (timestamp) {
    const {
      WMS: weightValueData
    } = trailerData || {};
    const userUnits      = getUserUnit();
    const {value, units} = convertWeightSensorData(weightValueData, userUnits);
    const today          = getTimeDifference(timestamp);
    const messageDate    = dateFormatter(timestamp, UserPreferences.getSystemDateFormat());
    const messageTime    = isNil(today) ? timeFormatter(timestamp, UserPreferences.getSystemTimeFormat()) : today;
    const label          = translate('common:smarterTrailer.weightSensor.label');
    const style          = {color: dlsColor('grey1'), fontWeight: 'bold'};

    return (
      <WidgetContainer>
        <WeightWidgetIcon />
        <WidgetDescription label={label}
          value={`${value} ${units}`}
          messageTime={messageTime}
          messageDate={messageDate}
          valueStyle={style} />
      </WidgetContainer>
    );
  }
  return null;
};

export default WeightWidget;
